import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { useAuth } from '../../../contexts/AuthContext';
import { useSubscription } from '../../../contexts/SubscriptionContext';
import { useToast } from '../../Toast';
import { classifyOverlapKinds, refineSaenggibuContent, updateBatchSaenggibuResult } from '../../../api/saenggibuApi';
import SaenggibuResultMoreMenu from '../../saenggibu/SaenggibuResultMoreMenu';
import SaenggibuSuggestionMount from '../../saenggibu/SaenggibuSuggestionMount';
import useSaenggibuSuggestion from '../../../hooks/useSaenggibuSuggestion';
import {
  resolveSaenggibuSuggestionRecordId,
  runSaenggibuSuggestionAction,
} from '../../../utils/saenggibuSuggestion';
import { regenerateBatchSaenggibu, getFriendlyBatchErrorMessage } from '../../../api/chatApi';
import { calculateNEISBytes, getSaenggibuMaxBytes } from '../../../utils/neisBytes';
import { resolveRegenerationInstructionChain } from '../../../utils/saenggibuRegeneration';
import { trackSaenggibuUxEvent } from '../../../utils/eventTracker';
import SaenggibuContentEditor from '../../saenggibu/SaenggibuContentEditor';
import { getBatchCategoryLabel } from './Step1_ExcelUpload';
import ClassCheckPanel from './ClassCheckPanel';
import { overlapFixTargetBytes, planClassOverlap } from '../../../utils/classOverlapFix';
import RegenerationChainList from '../../saenggibu/RegenerationChainList';
import RosterResultCard from './RosterResultCard';
import useSaenggibuEvidence from '../../saenggibu/evidence/useSaenggibuEvidence';
import SaenggibuEvidenceText, { SaenggibuEvidenceSummary, useShowEvidenceNumbers } from '../../saenggibu/evidence/SaenggibuEvidenceText';
import SaenggibuEvidenceDialog from '../../saenggibu/evidence/SaenggibuEvidenceDialog';
import './Step5_Results.css';

const Step5_Results = ({
  processingResults,
  onReplaceResults,
  onBack,
  onStartNew,
  onResetAll,
  selectedModel,
}) => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { subscriptionStatus } = useSubscription();
  const isProUser = Boolean(subscriptionStatus?.isPro);
  const { showToast, removeToast } = useToast();
  const [results, setResults] = useState(
    () => (processingResults || {})?.results || []
  );
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editedContent, setEditedContent] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [refineIndex, setRefineIndex] = useState(null);
  const [refinePresetId, setRefinePresetId] = useState('batch-strength');
  const [refineMode, setRefineMode] = useState('tone_up');
  const [refineInstruction, setRefineInstruction] = useState('');
  const [refinePreview, setRefinePreview] = useState(null);
  const [refinePreviewDraft, setRefinePreviewDraft] = useState('');
  const [refinePreviewEditing, setRefinePreviewEditing] = useState(false);
  const [refineLoadingIndex, setRefineLoadingIndex] = useState(null);
  const [refineUndo, setRefineUndo] = useState(null);
  const [regenerateOpen, setRegenerateOpen] = useState(true);
  const [regenerateInstruction, setRegenerateInstruction] = useState('');
  const [regeneratePresetId, setRegeneratePresetId] = useState(null);
  const [regenerateLoading, setRegenerateLoading] = useState(false);
  const [regenerateRetryCount, setRegenerateRetryCount] = useState(0);
  const [previousProcessingResults, setPreviousProcessingResults] = useState(null);
  const [manuallyEditedIndexes, setManuallyEditedIndexes] = useState(() => new Set());
  const [classFixProgress, setClassFixProgress] = useState(null);
  const [overlapKindBySentence, setOverlapKindBySentence] = useState({});
  const overlapKindRequestedRef = useRef(new Set());

  const recordId = processingResults?.record_id;
  const suggestionRecordId = resolveSaenggibuSuggestionRecordId(processingResults, { isBatch: true });
  const historySaved = processingResults?.history_saved ?? Boolean(recordId);
  const suggestionApi = useSaenggibuSuggestion({
    recordId: suggestionRecordId,
    category: processingResults?.options?.category || results[0]?.category,
    isBatch: true,
    enabled: Boolean(suggestionRecordId && currentUser),
  });
  const historyError = processingResults?.history_error;
  // 선생님이 고친 요청 목록은 지금 보고 있는 기록에만 붙인다(다른 기록으로 바뀌면 자동으로 버려짐).
  const [chainEdit, setChainEdit] = useState(null);
  const chainEdited = Boolean(chainEdit && chainEdit.recordId === recordId);
  const regenerationInstructionChain = chainEdited
    ? chainEdit.items
    : resolveRegenerationInstructionChain(processingResults);

  const getResultContent = (result) => result?.generated_content || result?.content || '';

  // 근거 보기: 펼친 학생(과 팝업에서 다음 학생)만 불러온다. 한 번 불러온 학생은 남겨 둔다.
  const [evidenceIndex, setEvidenceIndex] = useState(null);
  const [evidenceSentence, setEvidenceSentence] = useState(null);
  const [showEvidenceNumbers, setShowEvidenceNumbers] = useShowEvidenceNumbers();
  const evidenceOrder = useMemo(
    () => results.map((result, index) => (getResultContent(result).trim() ? index : null)).filter((index) => index !== null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [results],
  );
  const evidenceFocus = evidenceIndex ?? expandedIndex;
  const evidenceEntries = useMemo(() => {
    if (evidenceFocus === null || evidenceFocus === undefined) return [];
    const position = evidenceOrder.indexOf(evidenceFocus);
    const wanted = [evidenceFocus, evidenceIndex !== null ? evidenceOrder[position + 1] : null].filter((index) => index !== null && index !== undefined);
    return wanted.map((index) => ({ key: `s${index}`, content: getResultContent(results[index]) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evidenceFocus, evidenceIndex, evidenceOrder, results]);
  const evidenceApi = useSaenggibuEvidence({
    recordId: historySaved ? recordId : null,
    entries: evidenceEntries,
    enabled: Boolean(recordId && historySaved && currentUser) && !regenerateLoading,
  });
  const evidenceFor = (index) => evidenceApi.get(`s${index}`, getResultContent(results[index]));

  const getResultByteClass = (result) => {
    if (result?.is_over_limit) return 'over';
    return '';
  };

  const buildByteQualityFields = (byteCount, targetBytes) => {
    if (!targetBytes || targetBytes <= 0) {
      return {
        is_over_limit: false,
        is_under_minimum: false,
        is_under_preferred: false,
        quality_warnings: []
      };
    }
    const isOverLimit = byteCount > targetBytes;
    const isUnderMinimum = byteCount < Math.floor(targetBytes * 0.9);
    const isUnderPreferred = byteCount < Math.floor(targetBytes * 0.93);
    const qualityWarnings = [];
    if (isUnderMinimum) {
      qualityWarnings.push(`목표의 90% 미만입니다 (${byteCount}/${targetBytes}B).`);
    } else if (isUnderPreferred) {
      qualityWarnings.push(`권장 하한 93% 미만입니다 (${byteCount}/${targetBytes}B).`);
    }
    return {
      is_over_limit: isOverLimit,
      is_under_minimum: isUnderMinimum,
      is_under_preferred: isUnderPreferred,
      quality_warnings: qualityWarnings
    };
  };

  const getRegenerationStudents = () => {
    const inputContext = processingResults?.batch_input_context || {};
    const sourceStudents = Array.isArray(inputContext.students)
      ? inputContext.students
      : Array.isArray(processingResults?.input_students)
        ? processingResults.input_students
        : [];

    if (!sourceStudents.length && results.length) {
      return results.map((result, index) => ({
        id: result.student_id || `student_${index + 1}`,
        name: result.student_name || `학생${index + 1}`,
        existing_content: getResultContent(result),
        content: getResultContent(result),
        characteristics: '',
        included_columns: [],
        ...(result.roster_sid ? { roster_sid: result.roster_sid } : {}),
      }));
    }

    return sourceStudents.map((student, index) => ({
      id: student.id || student.student_id || `student_${index + 1}`,
      name: student.name || student.student_name || results[index]?.student_name || `학생${index + 1}`,
      existing_content: student.existing_content || student.content || student.characteristics || '',
      content: student.content || student.existing_content || '',
      characteristics: student.characteristics || '',
      included_columns: student.included_columns || [],
      ...(student.roster_sid ? { roster_sid: student.roster_sid } : {}),
    }));
  };

  const getRegeneratePresets = () => {
    const category = processingResults?.options?.category || results[0]?.category || '행동발달';
    const common = [
      {
        id: 'plain-tone',
        label: '더 담백하게',
        instruction: '전체적으로 과한 칭찬과 수식어를 줄이고, 담백한 생활기록부 문체로 다시 써줘.'
      },
      {
        id: 'specific-actions',
        label: '구체 행동 살리기',
        instruction: '추상적인 성실함보다 학생별 관찰 내용에 있는 구체 행동, 역할, 과정을 더 잘 보이게 다시 써줘.'
      },
      {
        id: 'student-difference',
        label: '학생별 차이 크게',
        instruction: '학생마다 문장 구조와 표현이 비슷하지 않게, 각 학생의 고유한 특징이 분명히 다르게 보이도록 다시 써줘.'
      },
      {
        id: 'fit-bytes',
        label: '분량 맞추기',
        instruction: '학생별 분량이 너무 들쭉날쭉하지 않게 목표 바이트에 맞춰 균형 있게 다시 써줘.'
      },
    ];

    const byCategory = {
      '행동발달': [
        {
          id: 'behavior-growth',
          label: '성장 과정 보이게',
          instruction: '좋은 학생이라는 결론보다 평소 행동의 변화, 관계 속 기여, 성장 과정이 자연스럽게 드러나게 다시 써줘.'
        },
        {
          id: 'behavior-balanced',
          label: '아쉬운 점도 자연스럽게',
          instruction: '너무 완벽한 학생처럼 쓰지 말고, 아쉬운 점은 낙인찍지 않으면서 성장 가능성이 보이게 자연스럽게 반영해줘.'
        },
      ],
      '교과': [
        {
          id: 'subject-thinking',
          label: '사고 과정 보이게',
          instruction: '활동을 했다는 나열보다 학생이 어떤 의문을 갖고, 어떻게 탐구하고, 어떤 방식으로 개념을 이해했는지 사고 흐름이 보이게 다시 써줘.'
        },
        {
          id: 'subject-criteria',
          label: '성취기준 연결',
          instruction: '평가기준이나 성취수준이 학생별 관찰 내용과 자연스럽게 연결되도록 다시 써줘. 단, 참고자료 문구만 풀어쓰지 말아줘.'
        },
      ],
      '교과_고등': [
        {
          id: 'high-thinking',
          label: '사고 과정 보이게',
          instruction: '제출물을 요약하지 말고 의문, 근거, 비교·분석, 후속 탐구가 보이게 다시 써줘.'
        },
        {
          id: 'high-criteria',
          label: '성취기준 연결',
          instruction: '평가계획서는 깊이만 참고하고, 학생 제출물의 구체 사실이 본문 중심이 되게 다시 써줘.'
        },
      ],
      '동아리': [
        {
          id: 'club-role',
          label: '역할·기여 중심',
          instruction: '참여 사실보다 동아리에서 맡은 역할, 산출물, 협력 과정이 잘 보이게 다시 써줘.'
        },
        {
          id: 'club-less-list',
          label: '활동 나열 줄이기',
          instruction: '여러 활동을 줄줄이 나열하지 말고 핵심 역할과 학생별 특징이 보이도록 정리해서 다시 써줘.'
        },
      ],
      '창체': [
        {
          id: 'creative-role',
          label: '역할·기여 중심',
          instruction: '참여 사실보다 활동 속에서 맡은 역할, 공동체에 기여한 점, 실제 행동이 잘 보이게 다시 써줘.'
        },
        {
          id: 'creative-less-list',
          label: '활동 나열 줄이기',
          instruction: '여러 활동을 줄줄이 나열하지 말고 핵심 활동과 학생별 특징이 보이도록 정리해서 다시 써줘.'
        },
      ],
      '진로': [
        {
          id: 'career-process',
          label: '탐색 흐름 살리기',
          instruction: '진로를 확정한 느낌보다 관심 분야를 탐색해가는 과정, 구체 활동, 후속 연결이 보이게 다시 써줘.'
        },
        {
          id: 'career-no-overclaim',
          label: '진로 단정 줄이기',
          instruction: '희망 진로나 역량을 과하게 단정하지 말고, 관찰 가능한 탐색 활동과 준비 과정을 중심으로 다시 써줘.'
        },
      ],
    };

    return [...(byCategory[category] || []), ...common];
  };

  const getBatchUxMeta = (extra = {}) => ({
    saenggibu_category: processingResults?.options?.category || results[0]?.category || '행동발달',
    is_batch: true,
    ...extra,
  });

  const handleSelectRegeneratePreset = (preset) => {
    setRegeneratePresetId(preset.id);
    setRegenerateInstruction(preset.instruction);
    trackSaenggibuUxEvent('saenggibu_regenerate_preset', getBatchUxMeta({
      preset_id: preset.id,
    }));
  };

  const buildRegenerationOptions = (maskedInstruction) => {
    const inputContext = processingResults?.batch_input_context || {};
    const firstResult = results[0] || {};
    // eslint-disable-next-line no-unused-vars
    const { priorInstructionsOverride: _staleOverride, ...previousOptions } = processingResults?.options || {};
    return {
      ...previousOptions,
      category: processingResults?.options?.category || firstResult.category || '행동발달',
      wordCount: processingResults?.options?.wordCount || firstResult.target_bytes || 1500,
      teacherInstruction:
        processingResults?.options?.teacherInstruction ||
        inputContext.teacher_instruction ||
        '',
      referenceText:
        processingResults?.options?.referenceText ||
        inputContext.reference_text ||
        '',
      regenerationInstruction: maskedInstruction,
    };
  };

  const buildRegenerationMetadata = () => {
    const inputContext = processingResults?.batch_input_context || {};
    return {
      ...(processingResults?.metadata || {}),
      source_file_meta: inputContext.source_file_meta || processingResults?.metadata?.source_file_meta || {},
      structure_analysis: inputContext.structure_analysis || processingResults?.metadata?.structure_analysis || {},
      regenerated_from_record_id: recordId || null,
    };
  };

  const buildBatchRefineContext = (index, result) => {
    const inputContext = processingResults?.batch_input_context || {};
    const sourceStudent = Array.isArray(inputContext.students) ? inputContext.students[index] : null;
    const contextParts = [
      `[학생명]\n${result.student_name || ''}`,
      `[카테고리]\n${result.category || processingResults?.options?.category || ''}`,
      `[목표 바이트]\n${result.target_bytes || ''}`,
    ];

    if (sourceStudent?.content || sourceStudent?.existing_content) {
      contextParts.push(`[학생 원자료]\n${sourceStudent.content || sourceStudent.existing_content}`);
    }
    if (inputContext.reference_text) {
      contextParts.push(`[평가계획서 참고자료]\n${inputContext.reference_text}`);
    } else if (processingResults?.options?.referenceText) {
      contextParts.push(`[평가계획서 참고자료]\n${processingResults.options.referenceText}`);
    }
    if (inputContext.teacher_instruction) {
      contextParts.push(`[처음 생성 요청사항]\n${inputContext.teacher_instruction}`);
    } else if (processingResults?.options?.teacherInstruction) {
      contextParts.push(`[처음 생성 요청사항]\n${processingResults.options.teacherInstruction}`);
    }

    return contextParts.join('\n\n');
  };

  const getBatchRefinePresets = (category) => {
    const common = [
      {
        id: 'batch-shorter',
        mode: 'reduce_bytes',
        label: '조금만 줄여줘',
        instruction: '내용은 좋은데 조금 길어. 핵심은 유지하고 조금만 줄여줘.'
      },
      {
        id: 'batch-plain',
        mode: 'tone_neutral',
        label: '평범하게 정리',
        instruction: '좋은데 조금 과한 느낌이 있어. 평범한 생기부 문장처럼 자연스럽게 정리해줘.'
      },
    ];

    const byCategory = {
      '행동발달': [
        {
          id: 'batch-strength',
          mode: 'tone_up',
          label: '강점 더 살리기',
          instruction: '이 친구의 강점을 더 살려줘. 태도도 올바르고 잘하는 친구야.'
        },
        {
          id: 'batch-weakness',
          mode: 'tone_down',
          label: '아쉬운 점 반영',
          instruction: '너무 잘 써줬어. 그런데 이 친구는 태도에서 아쉬운 점이 있어. 너무 좋게만 쓰지 말고, 조금 안 좋은 부분도 자연스럽게 살려서 써줘.'
        },
        {
          id: 'batch-specific',
          mode: 'custom',
          label: '구체 행동 넣기',
          instruction: '성실하다는 말보다 평소에 보인 구체적인 행동이 보이게 해줘.'
        },
      ],
      '교과': [
        {
          id: 'batch-subject-attitude',
          mode: 'custom',
          label: '수업 태도 살리기',
          instruction: '수업 시간에 보여준 태도와 참여 모습이 더 잘 드러나게 해줘.'
        },
        {
          id: 'batch-subject-thinking',
          mode: 'custom',
          label: '사고 과정 보이게',
          instruction: '정답만 잘한 느낌보다 이 과목에서 어떻게 생각하고 접근했는지가 보이게 해줘.'
        },
        {
          id: 'batch-subject-normal',
          mode: 'tone_down',
          label: '보통 학생 톤',
          instruction: '너무 잘하는 학생처럼 보이진 않아. 평범하지만 수업을 따라가려는 모습이 자연스럽게 보이게 해줘.'
        },
      ],
      '교과_고등': [
        {
          id: 'batch-high-thinking',
          mode: 'custom',
          label: '탐구 과정 살리기',
          instruction: '제출물 요약보다 어떤 근거로 생각하고 어떤 결론을 냈는지가 보이게 해줘.'
        },
        {
          id: 'batch-high-criteria',
          mode: 'custom',
          label: '성취기준만 참고',
          instruction: '평가계획서 문구만 풀어쓰지 말고 학생 제출물의 구체 사실이 중심에 오게 해줘.'
        },
        {
          id: 'batch-high-normal',
          mode: 'tone_down',
          label: '보통 학생 톤',
          instruction: '너무 심화 탐구한 학생처럼 보이진 않아. 자료 범위 안에서 자연스럽게 써줘.'
        },
      ],
      '동아리': [
        {
          id: 'batch-club-role',
          mode: 'custom',
          label: '맡은 역할 살리기',
          instruction: '동아리에서 맡은 역할과 실제로 한 일이 더 잘 보이게 해줘.'
        },
        {
          id: 'batch-club-collab',
          mode: 'tone_up',
          label: '협력 과정 강조',
          instruction: '혼자 잘한 느낌보다 팀원과 역할을 나누고 의견을 맞춘 과정이 살아나게 해줘.'
        },
        {
          id: 'batch-club-passive',
          mode: 'tone_down',
          label: '소극적인 점 반영',
          instruction: '너무 주도적인 학생처럼 쓰진 말고, 조금 소극적인 모습도 자연스럽게 반영해서 써줘.'
        },
      ],
      '창체': [
        {
          id: 'batch-creative-role',
          mode: 'custom',
          label: '맡은 역할 살리기',
          instruction: '활동에서 맡은 역할과 실제로 한 일이 더 잘 보이게 해줘.'
        },
        {
          id: 'batch-creative-community',
          mode: 'tone_up',
          label: '공동체성 강조',
          instruction: '학급이나 공동체에 기여한 모습, 협력하고 배려한 모습이 더 살아나게 해줘.'
        },
        {
          id: 'batch-creative-passive',
          mode: 'tone_down',
          label: '소극적인 점 반영',
          instruction: '너무 적극적인 학생처럼 쓰진 말고, 조금 소극적인 모습도 자연스럽게 반영해서 써줘.'
        },
      ],
      '진로': [
        {
          id: 'batch-career-flow',
          mode: 'custom',
          label: '탐색 흐름 살리기',
          instruction: '진로가 정해진 느낌보다 탐색해가는 과정과 고민의 흐름이 보이게 해줘.'
        },
        {
          id: 'batch-career-interest',
          mode: 'tone_up',
          label: '관심 분야 연결',
          instruction: '학생의 관심 분야와 활동 내용이 자연스럽게 이어지게 해줘.'
        },
        {
          id: 'batch-career-unclear',
          mode: 'tone_neutral',
          label: '진로 아직 모호',
          instruction: '진로가 아직 뚜렷하진 않아. 단정하지 말고 탐색 중인 학생처럼 자연스럽게 써줘.'
        },
      ],
    };

    return [...(byCategory[category] || byCategory['행동발달']), ...common];
  };

  const updateResultContent = (index, content, revision = null) => {
    const updatedResults = [...results];
    const targetBytes = updatedResults[index]?.target_bytes || 0;
    const byteCount = calculateNEISBytes(content);
    const existingRevisionHistory = Array.isArray(updatedResults[index]?.revision_history)
      ? updatedResults[index].revision_history
      : [];
    const revisionHistory = revision
      ? [...existingRevisionHistory, revision].slice(-30)
      : existingRevisionHistory;
    updatedResults[index] = {
      ...updatedResults[index],
      generated_content: content,
      content,
      byte_count: byteCount,
      revision_history: revisionHistory,
      revision_count: revisionHistory.length,
      last_revision_instruction: revision?.instruction || updatedResults[index]?.last_revision_instruction || '',
      ...buildByteQualityFields(byteCount, targetBytes)
    };
    setResults(updatedResults);
  };

  const persistBatchContent = async (index, content, revision = null) => {
    if (!recordId || !historySaved) return false;
    await updateBatchSaenggibuResult(recordId, index, content, revision);
    return true;
  };

  // 토글 핸들러
  const handleToggle = (index) => {
    setExpandedIndex(expandedIndex === index ? null : index);
    setEditingIndex(null);
    setRefineIndex(null);
    setRefinePreview(null);
    setRefinePreviewDraft('');
    setRefinePreviewEditing(false);
  };

  // 수정 시작
  const handleStartEdit = (index, content) => {
    setEditingIndex(index);
    setEditedContent(content);
  };

  // 수정 저장
  const handleSaveEdit = async (index) => {
    const byteCount = calculateNEISBytes(editedContent);
    const updatedResults = [...results];
    const targetBytes = updatedResults[index]?.target_bytes || 0;
    updatedResults[index] = {
      ...updatedResults[index],
      generated_content: editedContent,
      content: editedContent,
      byte_count: byteCount,
      ...buildByteQualityFields(byteCount, targetBytes)
    };
    setResults(updatedResults);
    setManuallyEditedIndexes((prev) => new Set(prev).add(index));
    setEditingIndex(null);
    setEditedContent('');

    if (!recordId || !historySaved) {
      trackSaenggibuUxEvent('saenggibu_manual_edit', getBatchUxMeta({
        version_index: index,
      }));
      showToast('화면에는 반영했어요. 기록에는 저장되지 않아 엑셀로 저장해주세요', 'warning', 6000);
      return;
    }

    try {
      setIsSavingEdit(true);
      await updateBatchSaenggibuResult(recordId, index, editedContent);
      trackSaenggibuUxEvent('saenggibu_manual_edit', getBatchUxMeta({
        version_index: index,
      }));
      suggestionApi.refetch({ edited: true, versionIndex: index });
      showToast('수정한 내용을 기록에 저장했어요', 'success');
    } catch (error) {
      console.error('일괄 생기부 수정 저장 실패:', error);
      showToast('화면에는 반영했어요. 기록 저장은 다시 시도해주세요', 'warning', 6000);
    } finally {
      setIsSavingEdit(false);
    }
  };

  // 수정 취소
  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditedContent('');
  };

  const handleOpenRefine = (index) => {
    setEditingIndex(null);
    setRefineIndex(prev => (prev === index ? null : index));
    setRefinePreview(null);
    setRefineInstruction('');
  };

  const handleRequestRefine = async (index, overrides = {}) => {
    const result = results[index];
    const currentContent = getResultContent(result);
    if (!currentContent.trim()) return;

    const maskedContent = currentContent;
    const maskedInstruction = overrides.instruction ?? (refineInstruction || '');
    const originalContext = buildBatchRefineContext(index, result);

    try {
      setRefineLoadingIndex(index);
      const response = await refineSaenggibuContent({
        record_id: recordId,
        student_index: index,
        current_content: maskedContent,
        instruction: maskedInstruction,
        mode: overrides.mode || refineMode,
        scope: 'batch_student',
        category: result.category || processingResults?.options?.category || '행동발달',
        target_bytes: overrides.targetBytes || result.target_bytes || undefined,
        original_context: originalContext,
        revision_history: Array.isArray(result.revision_history) ? result.revision_history.slice(-10) : [],
        selected_model: selectedModel,
      });

      setRefinePreview({
        index,
        before: currentContent,
        after: response.content || '',
        byteCount: response.byte_count || calculateNEISBytes(response.content || ''),
        revision: response.revision || null,
      });
      setRefinePreviewDraft(response.content || '');
      setRefinePreviewEditing(false);
      showToast('AI가 수정안을 만들었어요', 'success');
    } catch (error) {
      console.error('일괄 생기부 AI 다듬기 실패:', error);
      showToast(error.message || '생기부 문장을 다듬지 못했어요', 'error');
    } finally {
      setRefineLoadingIndex(null);
    }
  };

  // 반 점검표의 '이 학생만 다르게': 그 학생을 펼치고 바로 수정안을 만든다(적용은 선생님이 고른다).
  const handleMakeDifferent = (index, instruction, reasons = []) => {
    setExpandedIndex(index);
    setEditingIndex(null);
    setRefineIndex(index);
    setRefinePresetId(null);
    setRefineMode('custom');
    setRefineInstruction(instruction);
    setRefinePreview(null);
    trackSaenggibuUxEvent('saenggibu_class_check_make_different', getBatchUxMeta({
      version_index: index,
      reasons: reasons.join(','),
    }));
    const result = results[index];
    handleRequestRefine(index, {
      instruction,
      mode: 'custom',
      targetBytes: overlapFixTargetBytes(getResultContent(result), result?.target_bytes || 0),
    });
  };

  // 반 점검표 '겹치지 않게 N명 고치기': 수정안을 바로 적용하고, 끝나면 토스트에서 한 번에 되돌릴 수 있다.
  const applyContentAt = (index, content, revision = null) => {
    setResults((prev) => {
      const next = [...prev];
      const current = next[index] || {};
      const targetBytes = current.target_bytes || 0;
      const byteCount = calculateNEISBytes(content);
      const history = Array.isArray(current.revision_history) ? current.revision_history : [];
      const revisionHistory = revision ? [...history, revision].slice(-30) : history;
      next[index] = {
        ...current,
        generated_content: content,
        content,
        byte_count: byteCount,
        revision_history: revisionHistory,
        revision_count: revisionHistory.length,
        last_revision_instruction: revision?.instruction || current.last_revision_instruction || '',
        ...buildByteQualityFields(byteCount, targetBytes),
      };
      return next;
    });
  };

  const handleFixAllOverlap = async (targets, summary = {}) => {
    if (!targets.length || classFixProgress) return;
    const startedAt = Date.now();
    const snapshot = targets.map(({ index }) => ({ index, content: getResultContent(results[index]) }));
    const fixed = [];
    let done = 0;
    setClassFixProgress({ done: 0, total: targets.length });
    trackSaenggibuUxEvent('saenggibu_class_check_fix_all', getBatchUxMeta({
      stage: 'start',
      student_count: targets.length,
      same_count: summary.sameGroups || 0,
      near_count: summary.nearGroups || 0,
      max_people: summary.maxPeople || 0,
      level: summary.severity || 'none',
    }));
    const queue = [...targets];
    const worker = async () => {
      while (queue.length) {
        const { index, instruction } = queue.shift();
        const result = results[index];
        const before = getResultContent(result);
        try {
          const response = await refineSaenggibuContent({
            record_id: recordId,
            student_index: index,
            current_content: before,
            instruction,
            mode: 'custom',
            scope: 'batch_student',
            category: result.category || processingResults?.options?.category || '행동발달',
            target_bytes: overlapFixTargetBytes(before, result.target_bytes || 0),
            original_context: buildBatchRefineContext(index, result),
            revision_history: Array.isArray(result.revision_history) ? result.revision_history.slice(-10) : [],
            selected_model: selectedModel,
          });
          const after = String(response?.content || '').trim();
          if (after && after !== before) {
            applyContentAt(index, after, response.revision || null);
            fixed.push(index);
            persistBatchContent(index, after, response.revision || null).catch(() => {});
          }
        } catch (error) {
          console.error('반 점검 고치기 실패:', error);
        }
        done += 1;
        setClassFixProgress({ done, total: targets.length });
      }
    };
    await Promise.all([worker(), worker(), worker()]);
    setClassFixProgress(null);
    trackSaenggibuUxEvent('saenggibu_class_check_fix_all', getBatchUxMeta({
      stage: 'done',
      student_count: targets.length,
      revised_count: fixed.length,
      elapsed_ms: Date.now() - startedAt,
    }));
    if (!fixed.length) {
      showToast('고치지 못했어요. 잠시 후 다시 시도해주세요', 'error');
      return;
    }
    const restore = snapshot.filter(({ index }) => fixed.includes(index));
    let toastId = null;
    toastId = showToast(`${fixed.length}명 글을 겹치지 않게 고쳤어요`, 'success', 8000, {
      label: '되돌리기',
      onClick: () => {
        restore.forEach(({ index, content }) => {
          applyContentAt(index, content);
          persistBatchContent(index, content).catch(() => {});
        });
        trackSaenggibuUxEvent('saenggibu_class_check_fix_all', getBatchUxMeta({
          stage: 'undo',
          revised_count: restore.length,
        }));
        if (toastId !== null && toastId !== undefined) removeToast?.(toastId);
      },
    });
  };

  const classCheckContext = [
    processingResults?.batch_input_context?.reference_text || processingResults?.options?.referenceText || '',
    processingResults?.batch_input_context?.teacher_instruction || processingResults?.options?.teacherInstruction || '',
  ].join('\n');
  const overlapCategory = processingResults?.options?.category || results[0]?.category || '';
  const overlapPlan = useMemo(
    () => planClassOverlap(results.map((result) => getResultContent(result)), {
      contextText: classCheckContext,
      category: overlapCategory,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [results, classCheckContext, overlapCategory],
  );
  const overlapTargetIndexes = useMemo(() => new Set(overlapPlan.targets.map((target) => target.index)), [overlapPlan]);

  // 걸린 문장이 공통 활동 설명·상투 표현·개인 서술 중 무엇인지 AI가 나눠 '고치기' 요청에 쓴다(실패해도 기본 요청으로 고침).
  useEffect(() => {
    if (!currentUser) return;
    const pending = overlapPlan.groups
      .map((group) => group.sample)
      .filter((sample) => !overlapKindRequestedRef.current.has(sample))
      .slice(0, 40);
    if (pending.length === 0) return;
    pending.forEach((sample) => overlapKindRequestedRef.current.add(sample));
    classifyOverlapKinds({
      category: overlapCategory,
      sentences: pending.map((text, id) => ({ id, text: text.slice(0, 400) })),
    }).then((kinds) => {
      const labeled = {};
      pending.forEach((text, id) => { if (kinds[String(id)]) labeled[text] = kinds[String(id)]; });
      if (Object.keys(labeled).length) setOverlapKindBySentence((prev) => ({ ...prev, ...labeled }));
    });
  }, [currentUser, overlapCategory, overlapPlan]);
  const overlapKinds = useMemo(() => Object.fromEntries(
    overlapPlan.groups
      .filter((group) => overlapKindBySentence[group.sample])
      .map((group) => [group.id, overlapKindBySentence[group.sample]]),
  ), [overlapKindBySentence, overlapPlan]);

  const renderOverlapContent = (index, content) => {
    const spans = overlapPlan.students[index]?.sentences || [];
    if (!content || spans.length === 0) return content || '내용이 없어요.';
    const parts = [];
    let cursor = 0;
    spans.forEach((span) => {
      if (span.start < cursor) return;
      if (span.start > cursor) parts.push(content.slice(cursor, span.start));
      parts.push(
        <mark
          key={span.start}
          className="saenggibu-batch-overlap-mark"
          title={`${span.people - 1}명과 ${span.tier === 'same' ? '같은' : '거의 같은'} 문장`}
        >
          {content.slice(span.start, span.end)}
        </mark>,
      );
      cursor = span.end;
    });
    parts.push(content.slice(cursor));
    return parts;
  };

  const handleApplyRefinePreview = async () => {
    if (!refinePreview || refinePreview.index === undefined || !refinePreviewDraft.trim()) return;
    const finalContent = refinePreviewDraft.trim();
    updateResultContent(refinePreview.index, finalContent, refinePreview.revision || null);
    setRefineUndo({
      index: refinePreview.index,
      content: refinePreview.before,
    });

    try {
      const saved = await persistBatchContent(refinePreview.index, finalContent, refinePreview.revision || null);
      if (saved) {
        showToast('수정안을 적용하고 기록에 저장했어요', 'success');
      } else {
        showToast('화면에는 적용했어요. 엑셀로 저장해주세요', 'warning', 6000);
      }
    } catch (error) {
      console.error('일괄 생기부 AI 수정 저장 실패:', error);
      showToast('화면에는 적용했어요. 기록 저장은 다시 시도해주세요', 'warning', 6000);
    }

    setRefinePreview(null);
    setRefinePreviewDraft('');
    setRefinePreviewEditing(false);
    setRefineIndex(null);
  };

  const handleUndoRefine = async () => {
    if (!refineUndo || refineUndo.index === undefined) return;
    updateResultContent(refineUndo.index, refineUndo.content);

    try {
      await persistBatchContent(refineUndo.index, refineUndo.content);
      showToast('이전 문장으로 되돌렸어요', 'info');
    } catch (error) {
      console.error('일괄 생기부 되돌리기 저장 실패:', error);
      showToast('화면에는 되돌렸어요. 기록 저장은 다시 시도해주세요', 'warning', 6000);
    }
    setRefineUndo(null);
  };

  const handleRegenerateAll = async () => {
    const instruction = regenerateInstruction.trim();
    if (regenerateLoading) {
      showToast('이미 다시 만들고 있어요', 'warning');
      return;
    }
    if (!instruction) {
      showToast('어떤 방향으로 다시 만들지 적어주세요', 'warning');
      return;
    }
    if (!currentUser) {
      showToast('로그인 후 다시 만들 수 있어요', 'warning');
      return;
    }
    const students = getRegenerationStudents();
    if (!students.length) {
      showToast('처음 입력한 학생 정보를 찾지 못했어요. 이전 단계에서 다시 생성해주세요', 'error');
      return;
    }

    if (!regeneratePresetId) {
      trackSaenggibuUxEvent('saenggibu_regenerate_custom', getBatchUxMeta());
    }

    const maskedInstruction = instruction;
    const regeneratedOptions = buildRegenerationOptions(maskedInstruction);
    const regeneratedMetadata = buildRegenerationMetadata();
    const previousSnapshot = processingResults;

    try {
      setRegenerateLoading(true);
      setRegenerateRetryCount(0);

      const authToken = await currentUser.getIdToken();
      if (chainEdited) {
        trackSaenggibuUxEvent('saenggibu_regen_chain_override_sent', getBatchUxMeta({
          surface: 'batch',
          chain_count: regenerationInstructionChain.length,
        }));
      }
      const response = await regenerateBatchSaenggibu({
        studentsData: students,
        options: chainEdited
          ? { ...regeneratedOptions, priorInstructionsOverride: regenerationInstructionChain }
          : regeneratedOptions,
        authToken,
        metadata: regeneratedMetadata,
        regenerationInstruction: maskedInstruction,
        regeneratedFromRecordId: recordId,
        onRetry: (attempt) => setRegenerateRetryCount(attempt),
      });

      const responseData = response?.data || response || {};
      const inputContext = processingResults?.batch_input_context || {};
      const nextInstructionChain = resolveRegenerationInstructionChain(
        { regeneration_instruction_chain: regenerationInstructionChain },
        maskedInstruction,
        responseData.regeneration_instruction_chain
      );
      const nextProcessingResults = {
        ...responseData,
        record_id: response.record_id || responseData.record_id,
        history_saved: Boolean(response.history_saved || responseData.history_saved),
        history_error: response.history_error || responseData.history_error,
        regeneration_instruction_chain: nextInstructionChain,
        options: regeneratedOptions,
        metadata: {
          ...regeneratedMetadata,
          is_regeneration: true,
        },
        batch_input_context: {
          ...inputContext,
          students,
          teacher_instruction: regeneratedOptions.teacherInstruction || '',
          reference_text: regeneratedOptions.referenceText || '',
          regeneration_instruction: maskedInstruction,
          regeneration_instruction_chain: nextInstructionChain,
          regenerated_from_record_id: recordId || null,
        },
      };

      setPreviousProcessingResults(previousSnapshot);
      setResults(nextProcessingResults.results || []);
      onReplaceResults?.(nextProcessingResults);
      setExpandedIndex(null);
      setEditingIndex(null);
      setRefineIndex(null);
      setRefinePreview(null);
      setRefinePreviewDraft('');
      setRefinePreviewEditing(false);
      setRegenerateInstruction('');
      setRegeneratePresetId(null);

      if (nextProcessingResults.history_saved) {
        window.dispatchEvent(new CustomEvent('saenggibuCreated'));
      }
      showToast('요청을 반영해 전체 생기부를 다시 만들었어요', 'success');
    } catch (error) {
      console.error('일괄 생기부 전체 재생성 실패:', error);
      showToast(getFriendlyBatchErrorMessage(error), 'error');
    } finally {
      setRegenerateLoading(false);
    }
  };

  const handleRestorePreviousResults = () => {
    if (!previousProcessingResults) return;
    setResults(previousProcessingResults.results || []);
    onReplaceResults?.(previousProcessingResults);
    setPreviousProcessingResults(null);
    setExpandedIndex(null);
    showToast('이전 결과로 되돌렸어요', 'info');
  };

  // 엑셀 다운로드
  const handleDownload = async () => {
    try {
      setIsDownloading(true);

      // 엑셀 데이터 생성
      const excelData = results.map(result => ({
        '학생명': result.student_name || '',
        '카테고리': result.category || '',
        '생기부 내용': result.generated_content || '',
        '바이트': result.byte_count || '',
        '목표 바이트': result.target_bytes || '',
        '초과 여부': result.is_over_limit ? '초과' : '적정'
      }));

      // 워크북 생성
      const ws = XLSX.utils.json_to_sheet(excelData);
      
      // 컬럼 너비 설정
      ws['!cols'] = [
        { wch: 10 },  // 학생명
        { wch: 10 },  // 카테고리
        { wch: 100 }, // 생기부 내용 (넓게)
        { wch: 8 },   // 바이트
        { wch: 10 },  // 목표 바이트
        { wch: 10 }   // 초과 여부
      ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, '생기부');

      // 파일명 생성
      const now = new Date();
      const dateStr = now.toISOString().slice(0, 10).replace(/-/g, '');
      const filename = `생기부_일괄생성_${dateStr}.xlsx`;

      // 다운로드
      XLSX.writeFile(wb, filename);
      showToast('다운로드 폴더에 저장되었어요', 'success', 10000);
      trackSaenggibuUxEvent('saenggibu_batch_export', getBatchUxMeta({
        record_id: recordId,
        student_count: results.length,
        over_count: results.filter((result) => result.is_over_limit).length,
        revised_count: results.filter((result) => Number(result.revision_count) > 0).length,
        target_bytes: continueBytes,
      }));

    } catch (error) {
      console.error('다운로드 실패:', error);
      showToast('다운로드에 실패했어요', 'error');
    } finally {
      setIsDownloading(false);
    }
  };

  const successCount = processingResults?.successful_count || 0;
  const totalCount = processingResults?.total_students || 0;
  const failedCount = processingResults?.failed_count || 0;
  const continueCategory = processingResults?.options?.category || results[0]?.category || '행동발달';
  const rosterResultNames = results.filter(result => !result.error).map(result => result.student_name);
  const continueBytes = Number(processingResults?.options?.wordCount || results[0]?.target_bytes) || 0;
  const continueSummaryLabel = continueBytes > 0
    ? `${getBatchCategoryLabel(continueCategory)} · ${continueBytes}B`
    : getBatchCategoryLabel(continueCategory);

  const requestEvidenceRevision = async (index, { scope, sentenceText, sentenceIndex, instruction, targetBytes }) => {
    const result = results[index];
    const response = await refineSaenggibuContent({
      record_id: recordId,
      student_index: index,
      current_content: getResultContent(result),
      instruction,
      mode: scope === 'sentence' ? 'sentence' : 'custom',
      scope: scope === 'sentence' ? 'sentence' : 'batch_student',
      sentence_text: scope === 'sentence' ? sentenceText : undefined,
      sentence_index: scope === 'sentence' ? sentenceIndex : undefined,
      category: result.category || processingResults?.options?.category || '행동발달',
      target_bytes: targetBytes,
      original_context: buildBatchRefineContext(index, result),
      revision_history: Array.isArray(result.revision_history) ? result.revision_history.slice(-10) : [],
      selected_model: selectedModel,
    });
    return response?.content || '';
  };

  const applyEvidenceRevision = async (index, nextContent) => {
    const revision = {
      mode: 'evidence_sentence',
      instruction: '근거 보기에서 문장을 고침',
      before_bytes: calculateNEISBytes(getResultContent(results[index])),
      after_bytes: calculateNEISBytes(nextContent),
    };
    updateResultContent(index, nextContent, revision);
    setManuallyEditedIndexes((prev) => new Set(prev).add(index));
    try {
      const saved = await persistBatchContent(index, nextContent, revision);
      if (saved) showToast('바꾼 문장을 기록에 저장했어요', 'success');
      return saved;
    } catch (error) {
      console.error('근거 보기 문장 저장 실패:', error);
      return false;
    }
  };

  const evidencePosition = evidenceIndex !== null ? evidenceOrder.indexOf(evidenceIndex) : -1;
  const moveEvidence = (step) => {
    const next = evidenceOrder[evidencePosition + step];
    if (next === undefined) return;
    setEvidenceSentence(null);
    setEvidenceIndex(next);
    setExpandedIndex(next);
  };

  return (
    <div className="saenggibu-batch-results">
      {evidenceIndex !== null && results[evidenceIndex] && (
        <SaenggibuEvidenceDialog
          open
          onClose={() => setEvidenceIndex(null)}
          subtitle={`${evidenceIndex + 1}. ${results[evidenceIndex].student_name || `학생${evidenceIndex + 1}`}`}
          content={getResultContent(results[evidenceIndex])}
          evidence={evidenceFor(evidenceIndex)}
          status={evidenceApi.statusOf(`s${evidenceIndex}`, getResultContent(results[evidenceIndex]))}
          initialSentence={evidenceSentence}
          nav={evidenceOrder.length > 1 ? {
            index: evidencePosition,
            total: evidenceOrder.length,
            onPrev: () => moveEvidence(-1),
            onNext: () => moveEvidence(1),
          } : null}
          canRevise={Boolean(recordId && historySaved) && !regenerateLoading && refineLoadingIndex === null}
          onRequestRevision={(payload) => requestEvidenceRevision(evidenceIndex, payload)}
          onApply={(next) => applyEvidenceRevision(evidenceIndex, next)}
          surface="batch"
        />
      )}
      <div className="saenggibu-batch-step-header">
        <h2>{successCount}명 생기부 완성!</h2>
      </div>

      <div className="saenggibu-batch-results-content">
        {/* 결과 요약 */}
        <div className="saenggibu-batch-result-summary">
          <div className="saenggibu-batch-result-stat success">
            <span className="saenggibu-batch-result-number">{successCount}</span>
            <span className="saenggibu-batch-result-label">성공</span>
          </div>
          <div className="saenggibu-batch-result-stat total">
            <span className="saenggibu-batch-result-number">{totalCount}</span>
            <span className="saenggibu-batch-result-label">전체</span>
          </div>
          {failedCount > 0 && (
            <div className="saenggibu-batch-result-stat failed">
              <span className="saenggibu-batch-result-number">{failedCount}</span>
              <span className="saenggibu-batch-result-label">실패</span>
            </div>
          )}
        </div>

        <SaenggibuSuggestionMount
          recordId={suggestionRecordId}
          isGenerating={false}
          isAnonymous={!currentUser}
          suggestionApi={suggestionApi}
          onAction={(action, nextSuggestion, extra = {}) => {
            runSaenggibuSuggestionAction({
              action,
              suggestion: nextSuggestion,
              act: suggestionApi.act,
              dismiss: suggestionApi.dismiss,
              option: extra.option || nextSuggestion?.default_option,
              text: action === 'save_example'
                ? (
                  extra.text
                  || getResultContent(results[nextSuggestion?.payload?.version_index ?? 0])
                  || getResultContent(results[0])
                  || ''
                )
                : undefined,
              showToast,
              navigate,
            });
          }}
        />

        {/* 기록 저장 안내 */}
        <div
          className={`saenggibu-batch-save-card ${historySaved ? 'saved' : 'warning'}`}
          title={historySaved ? '내 생기부 기록에서 다시 열고 복사할 수 있어요.' : undefined}
        >
          <div className="saenggibu-batch-save-icon">
            {historySaved ? (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
                <path d="M20 6L9 17l-5-5" />
              </svg>
            ) : (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
                <path d="M12 9v4" />
                <path d="M12 17h.01" />
                <path d="M10.3 3.9L1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
              </svg>
            )}
          </div>
          <div className="saenggibu-batch-save-copy">
            <h4>{historySaved ? '기록에 저장했어요' : '기록 저장을 확인하지 못했어요'}</h4>
            {!historySaved && (
              <p>{historyError || '먼저 엑셀로 저장해주세요. 화면을 닫으면 수정 내용이 남지 않을 수 있어요.'}</p>
            )}
          </div>
          {historySaved && (
            <button
              className="saenggibu-batch-history-btn"
              onClick={() => navigate('/saenggibu-history')}
            >
              기록 보기
            </button>
          )}
        </div>

        {/* 엑셀 다운로드 */}
        <button 
          className="saenggibu-batch-download-btn"
          onClick={handleDownload}
          disabled={isDownloading || successCount === 0}
          style={{
            opacity: isDownloading || successCount === 0 ? 0.5 : 1,
            cursor: isDownloading || successCount === 0 ? 'not-allowed' : 'pointer'
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="7 10 12 15 17 10"></polyline>
            <line x1="12" y1="15" x2="12" y2="3"></line>
          </svg>
          <span>엑셀 다운로드 ({successCount}명)</span>
        </button>

        <ClassCheckPanel
          results={results}
          plan={overlapPlan}
          kinds={overlapKinds}
          contextText={classCheckContext}
          category={continueCategory}
          busyIndex={refineLoadingIndex}
          fixProgress={classFixProgress}
          disabled={regenerateLoading}
          onMakeDifferent={handleMakeDifferent}
          onFixAll={handleFixAllOverlap}
        />

        {/* 학생별 결과 (전화번호부 스타일) */}
        <div className="saenggibu-batch-result-list">
          {results.map((result, index) => (
            <div 
              key={index} 
              className={`saenggibu-batch-result-item ${expandedIndex === index ? 'expanded' : ''}`}
            >
              {/* 학생 헤더 */}
              <div 
                className="saenggibu-batch-result-header"
                onClick={() => handleToggle(index)}
              >
                <div className="saenggibu-batch-result-header-left">
                  <span className="saenggibu-batch-result-number">{index + 1}</span>
                  <span className="saenggibu-batch-result-name">{result.student_name}</span>
                  {overlapTargetIndexes.has(index) && (
                    <span className="saenggibu-batch-overlap-dot" title="다른 학생과 겹치는 문장이 있어요" aria-label="겹치는 문장 있음" />
                  )}
                  <span className="saenggibu-batch-result-category">{result.category}</span>
                  <span className={`saenggibu-batch-result-bytes ${getResultByteClass(result)}`}>
                    {result.byte_count} / {result.target_bytes} 바이트
                  </span>
                </div>
                <div className="saenggibu-batch-result-header-right">
                  <svg 
                    width="20" 
                    height="20" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2"
                    className="saenggibu-batch-toggle-icon"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </div>
              </div>

              {/* 상세 내용 */}
              {expandedIndex === index && (
                <div className="saenggibu-batch-result-detail">
                  {editingIndex === index ? (
                    // 수정 모드
                    <SaenggibuContentEditor
                      isSaving={isSavingEdit}
                      maxBytes={getSaenggibuMaxBytes(
                        result.category || processingResults?.options?.category || '행동발달'
                      )}
                      onCancel={handleCancelEdit}
                      onChange={setEditedContent}
                      onSave={() => handleSaveEdit(index)}
                      rows={15}
                      targetBytes={result.target_bytes}
                      value={editedContent}
                      variant="spacious"
                    />
                  ) : (
                    // 보기 모드
                    <div className="saenggibu-batch-view-mode">
                      {(overlapPlan.students[index]?.sentences || []).length > 0 && (
                        <p className="saenggibu-batch-overlap-hint">밑줄 친 문장이 다른 학생 글과 겹쳐요</p>
                      )}
                      <div className="saenggibu-batch-content-text">
                        {(overlapPlan.students[index]?.sentences || []).length > 0
                          ? renderOverlapContent(index, result.generated_content || '')
                          : (
                            <SaenggibuEvidenceText
                              content={result.generated_content || ''}
                              evidence={evidenceFor(index)}
                              showNumbers={showEvidenceNumbers}
                              onOpen={(sentence) => { setEvidenceSentence(sentence); setEvidenceIndex(index); }}
                            />
                          )}
                      </div>
                      {recordId && historySaved && (
                        <SaenggibuEvidenceSummary
                          compact
                          status={evidenceApi.statusOf(`s${index}`, getResultContent(result))}
                          evidence={evidenceFor(index)}
                          onOpen={() => { setEvidenceSentence(null); setEvidenceIndex(index); }}
                          showNumbers={showEvidenceNumbers}
                          onToggleNumbers={setShowEvidenceNumbers}
                        />
                      )}
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginTop: '12px' }}>
                        <button
                          className="saenggibu-batch-edit-trigger-btn"
                          onClick={() => handleOpenRefine(index)}
                          disabled={regenerateLoading}
                          title={regenerateLoading ? '전체 결과를 다시 작성하는 중에는 개별 AI 다듬기를 사용할 수 없어요.' : undefined}
                          style={{
                            background: refineIndex === index ? '#e8f3ff' : undefined,
                            color: refineIndex === index ? 'var(--whatquiz-primary)' : undefined
                          }}
                        >
                          AI로 다듬기
                        </button>
                        {refineUndo?.index === index && (
                          <button
                            className="saenggibu-batch-edit-trigger-btn"
                            onClick={handleUndoRefine}
                            disabled={regenerateLoading}
                          >
                            이전 문장으로
                          </button>
                        )}
                        <SaenggibuResultMoreMenu
                          size="pill"
                          disabled={regenerateLoading}
                          items={[
                            {
                              key: 'manual_edit',
                              label: '내용 수정',
                              onSelect: () => handleStartEdit(index, result.generated_content || ''),
                            },
                          ]}
                          saveExample={{
                            text: result.generated_content || result.content || '',
                            category: result.category || processingResults?.options?.category,
                            wasEdited: manuallyEditedIndexes.has(index),
                            isPro: isProUser,
                            isBatch: true,
                            versionIndex: index,
                            disabled: regenerateLoading,
                          }}
                        />
                      </div>

                      {refineIndex === index && (
                        <div style={{
                          marginTop: '12px',
                          padding: '14px',
                          borderRadius: '14px',
                          background: '#f8f9fa',
                          border: '1px solid #eef0f3'
                        }}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
                            {getBatchRefinePresets(result.category || processingResults?.options?.category || '행동발달').map(option => (
                              <button
                                key={option.id}
                                type="button"
                                onClick={() => {
                                  setRefinePresetId(option.id);
                                  setRefineMode(option.mode);
                                  setRefineInstruction(option.instruction);
                                }}
                                disabled={refineLoadingIndex === index}
                                title={option.instruction}
                                style={{
                                  border: 'none',
                                  borderRadius: '999px',
                                  padding: '7px 11px',
                                  background: refinePresetId === option.id ? '#3182f6' : '#eef2f7',
                                  color: refinePresetId === option.id ? '#fff' : '#4e5968',
                                  fontSize: '12px',
                                  fontWeight: 600,
                                  cursor: refineLoadingIndex === index ? 'not-allowed' : 'pointer',
                                  opacity: refineLoadingIndex === index ? 0.65 : 1
                                }}
                              >
                                {option.label}
                              </button>
                            ))}
                          </div>
                          <textarea
                            value={refineInstruction}
                            onChange={(event) => {
                              setRefinePresetId(null);
                              setRefineMode('custom');
                              setRefineInstruction(event.target.value);
                            }}
                            placeholder="추가 요청이 있으면 적어주세요."
                            rows={3}
                            disabled={refineLoadingIndex === index}
                            style={{
                              width: '100%',
                              boxSizing: 'border-box',
                              resize: 'vertical',
                              border: '1px solid #dfe3e8',
                              borderRadius: '10px',
                              padding: '10px 12px',
                              fontSize: '13px',
                              lineHeight: 1.5,
                              outline: 'none',
                              color: '#191f28',
                              background: '#fff'
                            }}
                          />
                          <p style={{
                            margin: '6px 0 0',
                            fontSize: '11px',
                            color: '#8b95a1',
                            lineHeight: 1.5
                          }}>
                            문체, 어투, 분량, 꼭 살릴 표현까지 자유롭게 요청할 수 있어요.
                          </p>
                          <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                            <button
                              type="button"
                              onClick={() => handleRequestRefine(index)}
                              disabled={refineLoadingIndex === index}
                              style={{
                                border: 'none',
                                borderRadius: '10px',
                                padding: '9px 14px',
                                background: '#3182f6',
                                color: '#fff',
                                fontSize: '13px',
                                fontWeight: 700,
                                cursor: refineLoadingIndex === index ? 'not-allowed' : 'pointer',
                                opacity: refineLoadingIndex === index ? 0.65 : 1
                              }}
                            >
                              {refineLoadingIndex === index ? '다듬는 중...' : '요청하기'}
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setRefineIndex(null);
                                setRefinePreview(null);
                              }}
                              style={{
                                border: 'none',
                                borderRadius: '10px',
                                padding: '9px 14px',
                                background: '#eef2f7',
                                color: '#4e5968',
                                fontSize: '13px',
                                fontWeight: 600,
                                cursor: 'pointer'
                              }}
                            >
                              닫기
                            </button>
                          </div>

                          {refinePreview?.index === index && (
                            <div style={{
                              marginTop: '12px',
                              padding: '12px',
                              borderRadius: '12px',
                              background: '#fff',
                              border: '1px solid #e5e8eb'
                            }}>
                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '8px'
                              }}>
                                <span style={{ fontSize: '12px', fontWeight: 700, color: '#191f28' }}>
                                  수정안
                                </span>
                                <span style={{ fontSize: '11px', color: '#8b95a1' }}>
                                  {refinePreview.byteCount}B
                                </span>
                              </div>
                              {refinePreviewEditing ? (
                                <textarea
                                  value={refinePreviewDraft}
                                  onChange={(event) => setRefinePreviewDraft(event.target.value)}
                                  rows={6}
                                  style={{
                                    width: '100%',
                                    boxSizing: 'border-box',
                                    resize: 'vertical',
                                    border: '1px solid #dfe3e8',
                                    borderRadius: '10px',
                                    padding: '10px 12px',
                                    fontSize: '13px',
                                    lineHeight: 1.7,
                                    outline: 'none',
                                    color: '#191f28',
                                    background: '#fff'
                                  }}
                                />
                              ) : (
                                <div style={{
                                  whiteSpace: 'pre-wrap',
                                  fontSize: '13px',
                                  lineHeight: 1.7,
                                  color: '#333d4b'
                                }}>
                                  {refinePreviewDraft || refinePreview.after}
                                </div>
                              )}
                              <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                                <button
                                  type="button"
                                  onClick={() => setRefinePreviewEditing(prev => !prev)}
                                  style={{
                                    border: 'none',
                                    borderRadius: '10px',
                                    padding: '9px 14px',
                                    background: '#eef2f7',
                                    color: '#4e5968',
                                    fontSize: '13px',
                                    fontWeight: 600,
                                    cursor: 'pointer'
                                  }}
                                >
                                  {refinePreviewEditing ? '수정 끝내기' : '수정하기'}
                                </button>
                                <button
                                  type="button"
                                  onClick={handleApplyRefinePreview}
                                  style={{
                                    border: 'none',
                                    borderRadius: '10px',
                                    padding: '9px 14px',
                                    background: '#191f28',
                                    color: '#fff',
                                    fontSize: '13px',
                                    fontWeight: 700,
                                    cursor: 'pointer'
                                  }}
                                >
                                  적용하기
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setRefinePreview(null);
                                    setRefinePreviewDraft('');
                                    setRefinePreviewEditing(false);
                                  }}
                                  style={{
                                    border: 'none',
                                    borderRadius: '10px',
                                    padding: '9px 14px',
                                    background: '#eef2f7',
                                    color: '#4e5968',
                                    fontSize: '13px',
                                    fontWeight: 600,
                                    cursor: 'pointer'
                                  }}
                                >
                                  취소
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* 실패 항목 */}
        {failedCount > 0 && (
          <div className="saenggibu-batch-error-section">
            <h4>처리 실패 항목</h4>
            <div className="saenggibu-batch-error-list">
              {processingResults?.errors?.map((error, index) => (
                <div key={index} className="saenggibu-batch-error-item">
                  <span className="saenggibu-batch-error-student">{error.student_name}</span>
                  <span className="saenggibu-batch-error-message">{error.error}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {regenerateOpen && (
        <div className="saenggibu-batch-regenerate-panel">
          <RegenerationChainList
            items={regenerationInstructionChain}
            onChange={(next) => setChainEdit({ recordId, items: next })}
            disabled={regenerateLoading}
            surface="batch"
            category={getBatchUxMeta().saenggibu_category}
          />
          <div className="saenggibu-batch-regenerate-presets">
            {getRegeneratePresets().map((preset) => (
              <button
                key={preset.id}
                type="button"
                className={`saenggibu-batch-regenerate-preset ${regeneratePresetId === preset.id ? 'active' : ''}`}
                onClick={() => handleSelectRegeneratePreset(preset)}
                disabled={regenerateLoading}
                title={preset.instruction}
              >
                {preset.label}
              </button>
            ))}
          </div>
          <textarea
            id="saenggibu-batch-regenerate-instruction"
            className="saenggibu-batch-regenerate-textarea"
            value={regenerateInstruction}
            onChange={(event) => {
              setRegenerateInstruction(event.target.value);
              setRegeneratePresetId(null);
            }}
            placeholder="프리셋을 고르거나 직접 요청을 적어주세요."
            rows={3}
            disabled={regenerateLoading}
          />
          {regenerateRetryCount > 0 && (
            <div className="saenggibu-batch-regenerate-retry">
              서버가 바빠 다시 시도하고 있어요. {regenerateRetryCount}번째 재시도 중입니다.
            </div>
          )}
          {regenerateLoading && (
            <div className="saenggibu-batch-regenerate-loading">
              <div className="saenggibu-batch-regenerate-loading-copy">
                <strong>AI가 전체 결과를 다시 작성하고 있어요</strong>
                <span>학생 수와 제출물 길이에 따라 몇 분 정도 걸릴 수 있어요. 기존 결과는 완료 전까지 그대로 유지됩니다.</span>
              </div>
              <div className="saenggibu-batch-regenerate-loading-bar">
                <span />
              </div>
            </div>
          )}
          <div className="saenggibu-batch-regenerate-actions">
            <button
              type="button"
              className="saenggibu-batch-regenerate-secondary"
              onClick={() => setRegenerateOpen(false)}
              disabled={regenerateLoading}
            >
              닫기
            </button>
            <button
              type="button"
              className="saenggibu-batch-regenerate-primary"
              onClick={handleRegenerateAll}
              disabled={regenerateLoading || !regenerateInstruction.trim()}
              title="기존 결과는 성공할 때까지 그대로 유지돼요."
            >
              {regenerateLoading ? `${successCount || totalCount}명 다시 작성 중...` : 'AI 재요청하기'}
            </button>
          </div>
        </div>
      )}

      {previousProcessingResults && (
        <button
          type="button"
          className="saenggibu-batch-regenerate-restore"
          onClick={handleRestorePreviousResults}
          disabled={regenerateLoading}
        >
          이전 결과로 돌아가기
        </button>
      )}

      {!regenerateLoading && (
        <RosterResultCard
          names={rosterResultNames}
          category={continueCategory}
          surface="batch"
        />
      )}

      {/* 후속 흐름: 프로덕션 분석상 다중 후속 생성의 91%가 같은 종류·같은 설정으로 "다음 학급"을 만든다.
          주 동작은 설정을 유지한 채 학생 자료만 비우고, 전부 초기화는 보조 링크로 뒀다. */}
      <div className="saenggibu-batch-step-navigation">
        <button
          type="button"
          className="saenggibu-batch-nav-btn saenggibu-batch-back-btn"
          onClick={() => {
            trackSaenggibuUxEvent('saenggibu_batch_back_to_input', getBatchUxMeta());
            onBack?.();
          }}
          disabled={regenerateLoading}
        >
          입력으로 돌아가기
        </button>

        <button
          type="button"
          className="saenggibu-batch-nav-btn saenggibu-batch-next-btn"
          onClick={() => {
            trackSaenggibuUxEvent('saenggibu_batch_next_class', getBatchUxMeta());
            onStartNew?.();
          }}
          disabled={regenerateLoading}
          title={`${continueSummaryLabel} 설정 그대로 다음 학급 파일을 올려요`}
        >
          다음 학급 만들기
        </button>
      </div>
      <p className="saenggibu-batch-next-class-hint">
        {continueSummaryLabel} 설정을 그대로 이어서 만들어요.
        {typeof onResetAll === 'function' && (
          <>
            {' '}
            <button
              type="button"
              className="saenggibu-batch-reset-all-link"
              onClick={() => {
                trackSaenggibuUxEvent('saenggibu_batch_reset_all', getBatchUxMeta());
                onResetAll();
              }}
              disabled={regenerateLoading}
            >
              설정도 초기화하고 새로 시작
            </button>
          </>
        )}
      </p>
    </div>
  );
};

export default Step5_Results;
